/// JobList: Filterable list of engine jobs with state filter tabs.
/// Shows all/pending/active/completed/failed jobs with selection support.
/// Selecting a job displays its detail view with complete/fail actions.

import { useState, useMemo } from "react";
import { useEngineStore } from "@/stores/engineStore";
import type { Job } from "@/types/engine";
import { JobDetail } from "./JobDetail";

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

type JobFilter = "all" | "activatable" | "activated" | "completed" | "failed";

const FILTER_LABELS: Record<JobFilter, string> = {
  all: "All",
  activatable: "Pending",
  activated: "Active",
  completed: "Done",
  failed: "Failed",
};

// ---------------------------------------------------------------------------
// State color dots
// ---------------------------------------------------------------------------

const STATE_DOT_COLORS: Record<string, string> = {
  created: "bg-zinc-400",
  activatable: "bg-blue-400",
  activated: "bg-yellow-400",
  completed: "bg-green-400",
  failed: "bg-red-400",
  timed_out: "bg-orange-400",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JobList() {
  const jobs = useEngineStore((s) => s.jobs);
  const [filter, setFilter] = useState<JobFilter>("all");
  const [selectedJobKey, setSelectedJobKey] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (filter === "all") return jobs;
    return jobs.filter((j) => j.state === filter);
  }, [jobs, filter]);

  const selectedJob = useMemo(
    () => jobs.find((j) => j.jobKey === selectedJobKey) ?? null,
    [jobs, selectedJobKey],
  );

  const countByState = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const job of jobs) {
      counts[job.state] = (counts[job.state] ?? 0) + 1;
    }
    return counts;
  }, [jobs]);

  return (
    <div className="flex flex-col h-full">
      {/* Filter tabs */}
      <div className="flex border-b border-zinc-700 px-2 shrink-0">
        {(Object.keys(FILTER_LABELS) as JobFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-2 py-1 text-xs border-b-2 transition-colors ${
              filter === f
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {FILTER_LABELS[f]}
            {f !== "all" && countByState[f] ? (
              <span className="ml-1 text-[10px] text-zinc-500">({countByState[f]})</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Job rows */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filtered.length === 0 && (
          <div className="flex items-center justify-center py-8 text-xs text-zinc-500">
            No jobs {filter !== "all" ? `in ${FILTER_LABELS[filter]} state` : ""}
          </div>
        )}
        {filtered.map((job) => (
          <JobRow
            key={job.jobKey}
            job={job}
            selected={job.jobKey === selectedJobKey}
            onClick={() => setSelectedJobKey(job.jobKey === selectedJobKey ? null : job.jobKey)}
          />
        ))}
      </div>

      {/* Job detail panel */}
      {selectedJob && <JobDetail job={selectedJob} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// JobRow sub-component
// ---------------------------------------------------------------------------

function JobRow({ job, selected, onClick }: { job: Job; selected: boolean; onClick: () => void }) {
  const dotColor = STATE_DOT_COLORS[job.state] ?? "bg-zinc-400";
  const time = new Date(job.createdAt).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer border-b border-zinc-800 hover:bg-zinc-800/50 ${
        selected ? "bg-zinc-800 border-l-2 border-l-blue-500" : ""
      }`}
      onClick={onClick}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
      <span className="text-xs text-zinc-300 font-mono w-16 truncate shrink-0">
        {job.jobKey.slice(0, 8)}
      </span>
      <span className="text-xs text-zinc-400 flex-1 truncate">{job.jobType}</span>
      <span className="text-[10px] text-zinc-500 shrink-0">{time}</span>
    </div>
  );
}
